import { useState } from 'react';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  getDoc,
  runTransaction,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDb, getFunctions } from '../../firebase/utils';
import {
  LeaveRequestInput,
  LeaveRequest,
  LeaveApprovalInput,
  LeaveCancellationInput,
} from '../types/leaveRequest';
import { LeaveBalance } from '../types/leaveBalance';
import { LeaveType } from '../types/leaveType';
import { LeaveHistoryInput } from '../types/leaveHistory';
import { getQuarterFromDate } from '../types/personalHealingDay';
import { validateLeaveRequest, deductLeaveBalance, restoreLeaveBalance } from '../utils/leaveValidation';
import { useCurrentUser } from './useCurrentUser';
import { EMPLOYEE_STATUS } from '../constants/employeeStatus';

/**
 * V173: Cloud Function 호출 타입
 */
interface CreateLeaveRequestCFInput {
  leaveTypeCode: string;
  startDate: string;
  endDate: string;
  unit: string; // LeaveUnit (fullDay, halfDay, morningHalf, afternoonHalf)
  metadata?: {
    reason?: string;
    [key: string]: any;
  };
  override?: boolean; // V65: 충돌 감지 2-phase UX — override 재전송용
}

interface CreateLeaveRequestCFResult {
  success: boolean;
  leaveRequestId?: string;
  approvalRequestId?: string;
  warning?: string;
  error?: string;
  hasConflict?: boolean;        // V65: 충돌 감지 2-phase UX
  conflicts?: Array<{
    type: string;
    date: string;
    details: string;
    conflictingLeaveStatus?: string;
  }>;
  cannotOverride?: boolean;     // V65: pending/orgApproved/cancelPending은 override 불가
}

/**
 * V182: 휴가 취소 CF 타입 (즉시 취소)
 */
interface CancelLeaveRequestCFInput {
  leaveRequestId: string;
  reason?: string;
}

interface CancelLeaveRequestCFResult {
  success: boolean;
  error?: string;
}

/**
 * V185: 휴가 취소 요청 CF 타입 (승인 워크플로우)
 */
interface RequestCancelLeaveCFInput {
  leaveRequestId: string;
  reason?: string;
}

interface RequestCancelLeaveCFResult {
  success: boolean;
  approvalRequestId?: string;
  error?: string;
}

/**
 * V185: 휴가 취소 요청 입력 (승인 워크플로우)
 */
export interface LeaveRequestCancelInput {
  leaveRequestId: string;
  reason?: string;
}

export interface UseLeaveMutationsResult {
  createLeaveRequest: (input: LeaveRequestInput) => Promise<string>;
  approveLeaveRequest: (input: LeaveApprovalInput) => Promise<void>;
  rejectLeaveRequest: (input: LeaveApprovalInput) => Promise<void>;
  cancelLeaveRequest: (input: LeaveCancellationInput) => Promise<void>;
  /** V185: 승인된 휴가 취소 요청 (승인 워크플로우) */
  requestCancelLeave: (input: LeaveRequestCancelInput) => Promise<string>;
  isSubmitting: boolean;
  error: Error | null;
}

/**
 * 휴가 신청/승인/반려/취소 Mutations
 */
export function useLeaveMutations(): UseLeaveMutationsResult {
  const { user } = useCurrentUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * V173: 휴가 신청 생성 (Cloud Function 호출)
   *
   * **변경 사항:**
   * - FE 직접 Firestore 쓰기 → Cloud Function 호출
   * - 승인자 결정은 서버에서 처리 (Server Authority)
   * - approvalRequest도 서버에서 함께 생성
   * - 마이너스 연차 경고 반환
   */
  const createLeaveRequest = async (input: LeaveRequestInput): Promise<string> => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // V173: Cloud Function 호출
      const functions = getFunctions();
      const createLeaveRequestCF = httpsCallable<CreateLeaveRequestCFInput, CreateLeaveRequestCFResult>(
        functions,
        'createLeaveRequest'
      );

      const cfInput: CreateLeaveRequestCFInput = {
        leaveTypeCode: input.leaveTypeCode,
        startDate: input.startDate,
        endDate: input.endDate,
        unit: input.unit,
        metadata: input.metadata,
      };

      const result = await createLeaveRequestCF(cfInput);

      // V65: 충돌 감지 2-phase UX
      if (!result.data.success && result.data.hasConflict) {
        // override 불가능한 충돌 (pending/orgApproved/cancelPending)
        if (result.data.cannotOverride) {
          throw new Error(result.data.error || '기존 휴가 신청과 충돌합니다. 기존 신청을 먼저 처리해주세요.');
        }

        // override 가능한 충돌 — 사용자에게 확인
        const conflictDetails = (result.data.conflicts || [])
          .map(c => `• ${c.details}`)
          .join('\n');
        const confirmed = window.confirm(
          `${result.data.error}\n\n${conflictDetails}\n\n기존 기록을 무효화하고 휴가를 신청하시겠습니까?`
        );

        if (!confirmed) {
          throw new Error('사용자가 취소했습니다.');
        }

        // override=true로 재전송
        const overrideResult = await createLeaveRequestCF({ ...cfInput, override: true });

        if (!overrideResult.data.success) {
          throw new Error(overrideResult.data.error || '휴가 신청 실패');
        }

        if (overrideResult.data.warning) {
          console.warn('마이너스 연차 경고:', overrideResult.data.warning);
        }

        return overrideResult.data.leaveRequestId!;
      }

      if (!result.data.success) {
        throw new Error(result.data.error || '휴가 신청 실패');
      }

      // 마이너스 연차 경고 로그
      if (result.data.warning) {
        console.warn('마이너스 연차 경고:', result.data.warning);
      }

      return result.data.leaveRequestId!;
    } catch (err: any) {
      // Firebase Functions 에러 메시지 추출
      const errorMessage = err.message || err.details?.message || '휴가 신청 중 오류가 발생했습니다.';
      setError(new Error(errorMessage));
      throw new Error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 휴가 승인
   *
   * @deprecated V183: 이 함수는 FE에서 직접 Firestore 트랜잭션을 사용하는 레거시 코드입니다.
   * WorkApprovalsPage에서는 useApprovalMutations().approveRequest()를 사용하세요.
   * 이 함수는 MyLeavePage 레거시 호환용으로만 유지됩니다.
   *
   * **권장 대안:**
   * - 승인자 페이지: useApprovalMutations().approveRequest({ approvalRequestId, action: 'approve' })
   * - CF 경로: approveApprovalRequest → handleApprovalAction → processLeaveApproval
   */
  const approveLeaveRequest = async (input: LeaveApprovalInput): Promise<void> => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsSubmitting(true);
      setError(null);

      await runTransaction(getDb(), async (transaction) => {
        // 1. 휴가 신청 조회
        const requestRef = doc(getDb(), 'leaveRequests', input.leaveRequestId);
        const requestDoc = await transaction.get(requestRef);

        if (!requestDoc.exists()) {
          throw new Error('휴가 신청을 찾을 수 없습니다.');
        }

        const request = { id: requestDoc.id, ...requestDoc.data() } as LeaveRequest;

        // 2. 휴가 유형 조회
        const leaveTypeDoc = await transaction.get(doc(getDb(), 'leaveTypes', request.leaveTypeCode));
        if (!leaveTypeDoc.exists()) {
          throw new Error('휴가 유형을 찾을 수 없습니다.');
        }
        const leaveType = { ...leaveTypeDoc.data(), code: leaveTypeDoc.id } as LeaveType;

        // 3. 승인 상태 업데이트
        let newStatus: LeaveRequest['status'];
        const updateData: Partial<LeaveRequest> = {
          updatedAt: serverTimestamp() as any,
          updatedBy: user.profile.id,
        };

        if (input.approverRole === 'org') {
          updateData.orgApprovedAt = serverTimestamp() as any;
          updateData.orgApproverComment = input.comment;

          if (leaveType.approvalFlow === 'orgAndHr') {
            newStatus = 'orgApproved'; // HR 승인 대기
          } else {
            newStatus = 'approved'; // 최종 승인
          }
        } else if (input.approverRole === 'hr') {
          updateData.hrApprovedAt = serverTimestamp() as any;
          updateData.hrApproverComment = input.comment;
          newStatus = 'approved'; // 최종 승인
        } else {
          throw new Error('잘못된 승인자 역할입니다.');
        }

        updateData.status = newStatus;

        transaction.update(requestRef, updateData);

        // 4. 최종 승인 시 잔액 차감 및 이력 생성
        if (newStatus === 'approved') {
          const balanceRef = doc(getDb(), 'leaveBalances', request.employeeId);
          const balanceDoc = await transaction.get(balanceRef);

          if (!balanceDoc.exists()) {
            throw new Error('휴가 잔액을 찾을 수 없습니다.');
          }

          const balance = { id: balanceDoc.id, ...balanceDoc.data() } as LeaveBalance;

          // 잔액 차감
          const quarter = getQuarterFromDate(request.startDate);
          const deductedBalance = deductLeaveBalance(balance, leaveType, request.totalDays, quarter);

          transaction.update(balanceRef, {
            ...deductedBalance,
            updatedAt: serverTimestamp(),
            updatedBy: user.profile.id,
          });

          // 차감 정보 저장
          transaction.update(requestRef, {
            deduction: {
              balanceBefore: balance.annual?.remaining || balance.compensatory?.remaining || 0,
              balanceAfter:
                deductedBalance.annual?.remaining ||
                deductedBalance.compensatory?.remaining ||
                0,
              deductionType: leaveType.deductionType,
              deductedDays: request.totalDays,
            },
          });

          // 이력 생성 (Phase 6-2: source, referenceId 추가)
          const historyRef = doc(collection(getDb(), 'leaveHistories'));
          const historyData: Omit<LeaveHistoryInput, 'id'> & { createdAt: any } = {
            employeeId: request.employeeId,
            employeeName: request.employeeName,
            historyType: 'used',
            leaveTypeCode: request.leaveTypeCode,
            leaveTypeName: request.leaveTypeName,
            changeDays: -request.totalDays,
            balanceBefore: balance.annual?.remaining || balance.compensatory?.remaining || 0,
            balanceAfter:
              deductedBalance.annual?.remaining ||
              deductedBalance.compensatory?.remaining ||
              0,
            unit: request.unit,
            startDate: request.startDate,
            endDate: request.endDate,
            leaveRequestId: request.id,
            reason: `${request.leaveTypeName} 사용`,
            source: leaveType.deductionType === 'compensatory' ? 'usage' : undefined,
            referenceId: request.id,
            metadata: {
              approverId: user.profile.id,
              approverName: user.profile.displayName || user?.profile?.displayName || '',
            },
            createdAt: serverTimestamp(),
            createdBy: user.profile.id,
          };

          transaction.set(historyRef, historyData);

          // 5. 장기병가/육아휴직 승인 시 직원 상태 변경
          if (leaveType.specialRules?.changesEmployeeStatus) {
            const employeeRef = doc(getDb(), 'users', request.employeeId);
            transaction.update(employeeRef, {
              status: EMPLOYEE_STATUS.ON_LEAVE,
              updatedAt: serverTimestamp(),
              updatedBy: user.profile.id,
            });
          }
        }
      });

    } catch (err) {
      console.error('휴가 승인 실패:', err);
      setError(err as Error);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 휴가 반려
   *
   * @deprecated V183: 이 함수는 FE에서 직접 Firestore updateDoc을 사용하는 레거시 코드입니다.
   * WorkApprovalsPage에서는 useApprovalMutations().approveRequest()를 사용하세요.
   * 이 함수는 MyLeavePage 레거시 호환용으로만 유지됩니다.
   *
   * **권장 대안:**
   * - 승인자 페이지: useApprovalMutations().approveRequest({ approvalRequestId, action: 'reject' })
   * - CF 경로: rejectApprovalRequest → handleApprovalAction
   */
  const rejectLeaveRequest = async (input: LeaveApprovalInput): Promise<void> => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const requestRef = doc(getDb(), 'leaveRequests', input.leaveRequestId);

      await updateDoc(requestRef, {
        status: 'rejected',
        rejectionReason: input.comment || '승인 거부',
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: user.profile.id,
      });

    } catch (err) {
      console.error('휴가 반려 실패:', err);
      setError(err as Error);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * V182: 휴가 취소 (Cloud Function 호출)
   *
   * **변경 사항:**
   * - FE 직접 Firestore 트랜잭션 → Cloud Function 호출
   * - 권한 검증, 잔액 복구, 상태 업데이트 모두 서버에서 처리
   * - pending 상태: 본인만 취소 가능
   * - approved 상태: HR/Admin만 취소 가능
   */
  const cancelLeaveRequest = async (input: LeaveCancellationInput): Promise<void> => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // V182: Cloud Function 호출
      const functions = getFunctions();
      const cancelLeaveRequestCF = httpsCallable<CancelLeaveRequestCFInput, CancelLeaveRequestCFResult>(
        functions,
        'cancelLeaveRequest'
      );

      const cfInput: CancelLeaveRequestCFInput = {
        leaveRequestId: input.leaveRequestId,
        reason: input.cancellationReason,
      };

      const result = await cancelLeaveRequestCF(cfInput);

      if (!result.data.success) {
        throw new Error(result.data.error || '휴가 취소 실패');
      }

    } catch (err: any) {
      console.error('휴가 취소 실패:', err);
      // Firebase Functions 에러 메시지 추출
      const errorMessage = err.message || err.details?.message || '휴가 취소 중 오류가 발생했습니다.';
      setError(new Error(errorMessage));
      throw new Error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * V185: 승인된 휴가 취소 요청 (승인 워크플로우)
   *
   * 즉시 취소되지 않고 approvalRequest를 생성하여 승인 절차를 거칩니다.
   * - approved/orgApproved 상태의 휴가만 취소 요청 가능
   * - 본인만 취소 요청 가능
   *
   * @param input 취소 요청 입력 (leaveRequestId, reason?)
   * @returns 생성된 approvalRequestId
   */
  const requestCancelLeave = async (input: LeaveRequestCancelInput): Promise<string> => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const functions = getFunctions();
      const requestCancelLeaveCF = httpsCallable<RequestCancelLeaveCFInput, RequestCancelLeaveCFResult>(
        functions,
        'requestCancelLeave'
      );

      const cfInput: RequestCancelLeaveCFInput = {
        leaveRequestId: input.leaveRequestId,
        // V185: reason이 빈 문자열이면 undefined로 (Firestore undefined 저장 방지는 서버에서)
        reason: input.reason?.trim() || undefined,
      };

      const result = await requestCancelLeaveCF(cfInput);

      if (!result.data.success) {
        throw new Error(result.data.error || '휴가 취소 요청 실패');
      }

      return result.data.approvalRequestId!;
    } catch (err: any) {
      console.error('휴가 취소 요청 실패:', err);
      const errorMessage = err.message || err.details?.message || '휴가 취소 요청 중 오류가 발생했습니다.';
      setError(new Error(errorMessage));
      throw new Error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    createLeaveRequest,
    approveLeaveRequest,
    rejectLeaveRequest,
    cancelLeaveRequest,
    requestCancelLeave,
    isSubmitting,
    error,
  };
}
